/* eslint-disable no-underscore-dangle */
import {
  initialize,
  isInitialized,
  automaticallyShowInAppMessages,
  changeUser,
  requestImmediateDataFlush,
  openSession,
} from '@braze/web-sdk'
import { targetEnv } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'
import { i18n } from '@fs/zion-locale'
import { getUser } from '@fs/zion-user'

// Track the currently applied external ID so we don't repeat changeUser calls unnecessarily.
let currentExternalUserId = null

const debug = zionDebug('discovery:braze')

const BRAZE_BASE = 'sdk.iad-07.braze.com'
const BRAZE_API_KEY_DEV = 'b740c715-c82b-40e9-8b5d-be9e7dc01cd9'
const BRAZE_API_KEY_PROD = 'c796f063-1f32-407c-a29c-c9ae755fe907'
const BRAZE_API_KEY = targetEnv === 'prod' ? BRAZE_API_KEY_PROD : BRAZE_API_KEY_DEV

// Braze likes two character language codes
// eslint-disable-next-line no-underscore-dangle -- This function is prefixed with an underscore for internal use.
export function _mapLanguage(language) {
  if (language === 'zh-hant') {
    return 'zh'
  }
  // Make zh-hans undefined to play nice with `Accept-Language`/HTML lang attribute
  if (language === 'zh-hans') {
    return undefined
  }
  return language
}

// Initialize Braze.
export async function init() {
  debug('init() called. anonymous?', 'already initialized?', isInitialized())

  if (isInitialized()) {
    return true
  }

  let user
  let hasUserId = false
  try {
    user = await getUser()
    hasUserId = !!user?.cisId
    debug('braze init fetched user. hasUserId?', hasUserId)
  } catch (e) {
    debug('braze init getUser failed (continuing anonymous):', e)
  }

  if (!isInitialized()) {
    // Perform one-time SDK initialization.
    debug('Initializing Braze SDK now')
    const success = initialize(BRAZE_API_KEY, {
      baseUrl: BRAZE_BASE,
      localization: _mapLanguage(i18n.language),
      enableLogging: debug.enabled,
      manageServiceWorkerExternally: true,
      serviceWorkerLocation: `${window?.SERVER_DATA?.appPath || ''}/service-worker.js`,
    })
    debug('initialize returned:', success)

    automaticallyShowInAppMessages()
  }

  // If we have a user id and it's different from the current applied id, apply it.
  if (hasUserId && user?.cisId) {
    const externalId = user.cisId.split('.')[2]
    if (externalId && externalId !== currentExternalUserId) {
      debug('Applying identified user to Braze session', externalId)
      changeUser(externalId)
      currentExternalUserId = externalId
      // Flush to associate any queued events under the anonymous user with the identified profile.
      try {
        requestImmediateDataFlush()
      } catch (e) {
        debug('requestImmediateDataFlush failed (non-fatal)', e)
      }
    }
  }

  openSession()
  return true
}

export const __testables = { _mapLanguage }
